/**
 * Timezone utility functions
 * Handles formatting and lazy loading of timezone data
 */

export interface TimezoneData {
  tzCode: string
  label: string
  name: string
  baseOffset: number
  currentOffset: number
  isDST: boolean
  abbreviation: string
  country: string
  countryName: string
  region: string
  latitude: number
  longitude: number
  cities: string[]
}

/**
 * Format offset as GMT±HH:MM
 * @param offset - Offset in hours (e.g., -8, 5.5)
 */
function formatOffset(offset: number): string {
  const sign = offset >= 0 ? "+" : "-"
  const absOffset = Math.abs(offset)
  const hours = Math.floor(absOffset)
  const minutes = Math.round((absOffset - hours) * 60)

  const hoursStr = hours.toString().padStart(2, "0")
  const minutesStr = minutes.toString().padStart(2, "0")

  return `GMT ${sign}${hoursStr}:${minutesStr}`
}

/**
 * Format timezone for display
 * Format: GMT {offset} / {countryName}, {name}
 * Example: GMT -08:00 / United States, Los Angeles
 */
export function formatTimezoneLabel(tz: TimezoneData): string {
  const offset = formatOffset(tz.currentOffset)
  return `${offset} / ${tz.countryName}, ${tz.name}`
}

/**
 * Mapping of deprecated IANA timezone names to their modern equivalents
 */
const DEPRECATED_TIMEZONE_MAPPING: Record<string, string> = {
  "Asia/Calcutta": "Asia/Kolkata",
}

/**
 * Normalize a timezone string, mapping deprecated IANA names to modern equivalents
 */
export function normalizeTimezone(tz: string): string {
  return DEPRECATED_TIMEZONE_MAPPING[tz] || tz
}

/**
 * Get the device's timezone and normalize deprecated names to modern equivalents
 * Some browsers return deprecated timezone names (e.g., "Asia/Calcutta") but our
 * timezone data uses modern names (e.g., "Asia/Kolkata")
 */
export function getNormalizedTimezone(): string {
  const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  if (!deviceTimezone) {
    return "UTC"
  }

  return normalizeTimezone(deviceTimezone)
}

/**
 * Lazy load timezones from JSON file
 * Uses dynamic import to load only when needed
 */
let cachedTimezones: TimezoneData[] | null = null

export async function getTimezones(): Promise<TimezoneData[]> {
  if (cachedTimezones) {
    return cachedTimezones
  }

  const module = await import("./timezones.json")
  cachedTimezones = module.default as TimezoneData[]
  return cachedTimezones
}
